import _ from 'lodash';
import Vue from "vue";
import moment from "moment";
import Api from "services/Api";
import Store from "store";
import {validDate} from "components/form/validator/validations";
import {defaultIncomePeriodTemplate} from "store/modules/shared/templates";

function clone(life) {
    const cloned = {};
    Vue.util.extend(cloned, life);

    return life;
}

function roundYears(years) {
    return Math.round(years * 100) / 100;
}

/**
 * Cycle over an object and dispatch an update for each key.
 *
 * @param {function} dispatch
 * @param {string} action
 * @param {{}} obj
 * @param {number|null} [index]
 *
 * @return {Promise}
 */
export const updateObject = (dispatch, action, obj, index = null) => {
    const proms = Object.keys(obj).map(key => dispatch(action, {key, value: obj[key], index}));
    return Promise.all(proms);
};

/**
 * Filter to define variables that a user can set directly on a person (stop's nest properties being set)
 * @return {function(*): boolean}
 */
export const userSettable = key => key !== 'lifeExpectancy' && key !== 'income' && key !== 'ageAtDeath';

/**
 * When a field on a person updated, dispatch the commanding actions.
 * Action are defined as arguments to the first function, then the second function is the trigger
 *
 * @param {{[ageAtDeath]:string,incomeValue:string,retirementDate:string}} actions
 *
 * @return {function(*): Array<Promise>}} of updated to await
 */
export const dynamicUpdates = actions => (key, dispatch, person, index) => {
    const proms = [];
    switch (key) {
        case 'dateOfDeath':
        case 'dateOfBirth':
            // Update age at death, if required
            if (actions.ageAtDeath) {
                proms.push(dispatch(actions.ageAtDeath, {
                    key: 'ageAtDeath',
                    value: calcAgeDeath(person),
                    index,
                    force: true,
                }));
            }
            // Update life expectancy now dob has changed
            proms.push(dispatch(actions.lifeExpectancy, {lifeExpectancy: person.lifeExpectancy, index}));
            if (key == 'dateOfDeath') { // We don't need to check retirment date for date of death
                break;
            }
    }
    return proms;
};

/**
 * Find the next start date for a income period from start.
 *
 * @param periods
 * @param worseCase
 */
export const findNextPeriodStart = (periods, worseCase) => periods.reduce((min, period) => {
    const periodDate = moment(period.endOfPeriod, 'DD/MM/YYYY');
    return periodDate.isAfter(min) ? periodDate : min;
}, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');

/**
 * Find the next end date for a income period to end.
 *
 * @param periods
 * @param worseCase
 */
export const findNextPeriodEnd = (periods, worseCase) => {
    return periods.reduce((max, period) => {
        let periodDate = moment(period.startOfPeriod, 'DD/MM/YYYY');
        return periodDate.isAfter(max) ? periodDate : max;
    }, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');
};

/**
 * Check to see if the income period needs to update
 *
 * @param updatedKeys
 */
export const doesIncomePeriodNeedToUpdate = (updatedKeys) => {
    const triggerKeys = [
        'startOfPeriod',
        'endOfPeriod',
        'employmentType',
        'employmentIncome',
        'employmentNetGross',
        'pensionIncome',
        'pensionNetGross',
        'grossIncome',
        'netIncome',
        'netPensionIncome',
        'grossPensionIncome',
    ];
    const keys = updatedKeys.filter(key => key && ~triggerKeys.indexOf(key));
    return !!keys.length;
};

/**
 * If zeroes are provided then reset the totals to zero
 *
 * @param  {Function} (dispatch)
 * @param  {int} index
 * @param  {Object} period
 * @param  {Object} person
 * @param  {string} action
 * @param  {string} type
 */
export const zeroCheck = (dispatch, index, period, person, action, type) => {
    let proms = [];
    if (type === 'deceased') {
        if (person.income.retired === true && period.pensionIncome === 0) {
            proms.push(dispatch(action, {key: 'totalNetIncome', value: 0, index}));
        }
        if (person.income.retired === false && period.pensionIncome === 0 && period.employmentIncome === 0) {
            proms.push(dispatch(action, {key: 'totalNetIncome', value: 0, index}));
            proms.push(dispatch(action, {key: 'totalGrossIncome', value: 0, index}));
        }
    } else {
        if (person.income.retired === true && period.pensionIncome === 0) {
            proms.push(dispatch(action, {key: 'totalNetIncome', value: 0, index, dependant: person}));
        }
        if (person.income.retired === false && period.pensionIncome === 0 && period.employmentIncome === 0) {
            proms.push(dispatch(action, {key: 'totalNetIncome', value: 0, index, dependant: person}));
            proms.push(dispatch(action, {key: 'totalGrossIncome', value: 0, index, dependant: person}));
        }
    }
    return Promise.all(proms);
};

/**
 * Is a Valid Income Period Provided
 *
 * @param  {Object} period
 * @return {boolean}
 */
export const isValidIncome = (period) =>
    validDate(period.startOfPeriod) &&
    validDate(period.endOfPeriod) &&
    !isNaN(period.employmentIncome) &&
    !isNaN(period.pensionIncome) &&
    moment(period.startOfPeriod, 'DD/MM/YYYY').isBefore(
        moment(period.endOfPeriod, 'DD/MM/YYYY')
    );

/**
 * Perform a Throttle Income API request
 *
 * @param  {Function} (period, dispatch, action, args, person, dependant)
 * @return {Promise}
 */
export const throttledIncome = (period, dispatch, action, args, person) => {
    return Api.calculators.monetary.income(period, person)
        .then((updated) => {
            Object.keys(updated)
                .forEach((key) => {
                    if (updated[key] !== null) {
                        dispatch(action, {key, value: updated[key], ...args});
                    }
                });
        })
        .then(() => {
            return dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true})
        })
}

/**
 * Perform an Unthrottled Income API request
 * Used only for a updating an array of values. Never by the user
 *
 * @param  {Function} (period, dispatch, action, args, person)
 * @return {Promise}
 */
export const unthrottledIncome = (period, dispatch, action, args, person) => {
    Api.calculators.monetary.income(period, person)
        .then((updated) => {
            Object.keys(updated)
                .forEach((key) => {
                    if (updated[key] !== null) {

                        Store.dispatch(action, {
                            key,
                            value: updated[key], ...args
                        });
                    }
                });
        })
};

/**
 * Format the finance values for the API
 *
 * @param  person
 * @param  duration
 * @param  prefix
 *
 * @return {array}
 */
export const financeValues = (person, duration, prefix = '') => {
    const income = person.income;
    const life = lifeExpectancyValues(person);
    return {
        [`${prefix}retired_at_death`]: income.employmentType ? 'yes' : 'no',
        [`${prefix}pension_type`]: `${income.pensionIncomeType}_income`,
        [`${prefix}pension_value`]: income[`${income.pensionIncomeType}PensionIncome`],
        [`${prefix}pension_unchanged`]: income.unchangedPension,
        [`${prefix}employment_type`]: income.employmentType,
        [`${prefix}retired`]: income.retired,
        [`${prefix}retirement_age_calc`]: income.retirementAgeCalc,
        [`${prefix}retirement_age`]: income.retirementAge,
        [`${prefix}retirement_date`]: income.retirementDate,
        [`${prefix}net_income`]: income.netIncome || 0,
        [`${prefix}gross_income`]: income.grossIncome || 0,
        [`${prefix}net_pension_income`]: income.netPensionIncome || 0,
        [`${prefix}gross_pension_income`]: income.grossPensionIncome || 0,
        [`${prefix}date_of_birth`]: person.dateOfBirth,
        [`${prefix}date_of_death`]: person.lifeExpectancy.dateOfDeath,
        [`${prefix}type`]: life.type,
        [`${prefix}value`]: life.value,
        [`${prefix}period_of_dependency`]: duration,
        [`${prefix}periods`]: person.income.periods,
        [`${prefix}employed_at_date_of_death`]: person.employedAtDateOfDeath,
        [`${prefix}contingency_multiplier`]: income.contingencyMultiplier,
        [`${prefix}multiplier`]: income.multiplier,
        [`${prefix}level_of_education`]: person.levelOfEducation,
        [`${prefix}disabled_at_accident`]: person.disabledAtAccident
    };
};

const lifeExpectancyValues = function (person) {
    let leValue = null;
    const life = person.lifeExpectancy;
    switch (life.method) {
        case 'years_and_months':
            leValue = [life.yearsAndMonths.years, life.yearsAndMonths.months];
            break;
        case 'enter_date':
            leValue = life.dateOfDeathIsAssumed;
            break;
        case 'age_at_death':
            leValue = life.ageAtDeath;
            break;
        case 'percentage_of_normal_life_expectancy':
            leValue = life.percentageOfNormalLifeExpectancy;
            break;
        case 'percentage_reduction_in_normal_life_expectancy':
            leValue = life.percentageReductionInNormalLifeExpectancy;
            break;
        case 'dependent_until_16':
            leValue = [life.child.youthCutOffDate, life.child.youthCutOffAge, life.child.youthYearsLeft];
            break;
        case 'dependent_until_18':
            leValue = [life.child.youthCutOffDate, life.child.youthCutOffAge, life.child.youthYearsLeft];
            break;
        case 'dependent_until_21':
            leValue = [life.child.youthCutOffDate, life.child.youthCutOffAge, life.child.youthYearsLeft];
            break;
        case 'dependent_until_custom':
            leValue = [life.child.youthCutOffDate, life.child.youthCutOffAge, life.child.youthYearsLeft];
            break;
    }
    return {type: life.method, value: leValue};
};

/**
 * After the income on person, has been update, determine from updated keys other action to dispatch
 *
 * @param {{updateRetirementAge:string,updateRetirementDate:string, updateValue:string }} actions the action to person
 * @param {function} dispatch
 * @param {incomeTemplate} income
 * @param {string} dateOfDeath
 * @param {Number} [index]
 *
 * @return {function(*) : Array<Promise>}
 */
export const incomeUpdates = (actions, dispatch, income, dateOfDeath, index) => (updatedKeys) => {
    const proms = [];
    // If retirement date updates
    if (~updatedKeys.indexOf('retirementDate')) {
        proms.push(dispatch(actions.updateRetirementAge, {index}));
    }
    // If retirement age updates
    if (~updatedKeys.indexOf('retirementAge')) {
        proms.push(dispatch(actions.updateRetirementDate, {index}));
    }

    return Promise.all(proms);
};

/**
 *
 * @param person
 * @param lifeExpectancy
 * @return {{method: string, yearsAndMonths: {months: number, years: number}, dateOfDeathIsAssumed: (null|string), ageAtDeath: (null|number), dateOfDeath: (null|string)}}
 */
function calcYearsAndMonths(person, lifeExpectancy) {
    const dob = moment(person.dateOfBirth, 'DD/MM/YYYY');
    const end = person.dateOfDeath ? moment(person.dateOfDeath, 'DD/MM/YYYY') : moment();
    end.add(lifeExpectancy.yearsAndMonths.years, 'years')
        .add(lifeExpectancy.yearsAndMonths.months, 'months');

    return {
        dateOfDeath: end.format('DD/MM/YYYY'),
        ageAtDeath: roundYears(end.diff(dob, 'years', true)),
    };
}

function calcEnterDate(person, lifeExpectancy) {
    const dob = moment(person.dateOfBirth, 'DD/MM/YYYY');
    const end = moment(lifeExpectancy.dateOfDeathIsAssumed, 'DD/MM/YYYY');

    if (!validDate(lifeExpectancy.dateOfDeathIsAssumed)) {
        return {
            dateOfDeath: '',
            ageAtDeath: 0,
        };
    }

    return {
        dateOfDeath: end.format('DD/MM/YYYY'),
        ageAtDeath: roundYears(end.diff(dob, 'years', true)),
    };
}

function ogdenValue(assumedSettlement, person, lifeExpectancy) {
    const clonePerson = clone(person);

    lifeExpectancy.dateOfDeath = moment().format('DD/MM/YYYY');
    clonePerson.dateOfDeathAssumed = lifeExpectancy.dateOfDeathIsAssumed;

    return Api.calculators.dates.lifeExpectancy('ogden_table', clonePerson, assumedSettlement).then((result) => {
        const mDateOfDeath = moment(result.dateOfDeath, 'DD/MM/YYYY');

        return {
            dateOfDeath: mDateOfDeath.format('DD/MM/YYYY'),
            ageAtDeath: result.ageAtDeath,
        };
    });
}

function enterAgeAtDeath(person, lifeExpectancy) {
    if (lifeExpectancy.ageAtDeath > 0) {
        const dateOfDeath = moment(person.dateOfBirth, 'DD/MM/YYYY')
            .add(lifeExpectancy.ageAtDeath, 'years')
            .format('DD/MM/YYYY');
        return {
            dateOfDeath,
        };
    }
    return {
        dateOfDeath: '',
    };
}

let percentageOfNormalLifeExpectancy = (assumedSettlement, person, lifeExpectancy) => {
    if (lifeExpectancy.percentageOfNormalLifeExpectancy < 0 || lifeExpectancy.percentageOfNormalLifeExpectancy > 100) {
        throw {'error': 'Percentage of normal life expectancy must be between = and 100'};
    }

    return Api.calculators.dates.lifeExpectancy(
        'percentage_of_normal_life_expectancy',
        person,
        {
            percentage: lifeExpectancy.percentageOfNormalLifeExpectancy,
            date_of_trial: assumedSettlement,
        }
    ).then((data) => {
        return {
            dateOfDeath: data.dateOfDeath,
            ageAtDeath: data.ageAtDeath,
        };
    });
};

function percentageReductionInNormalLifeExpectancy(assumedSettlement, person, lifeExpectancy) {
    if (lifeExpectancy.percentageReductionInNormalLifeExpectancy < 0 || lifeExpectancy.percentageReductionInNormalLifeExpectancy > 100) {
        throw {'error': 'Percentage reduction in normal life expectancy must be between 0 and 100'};
    }

    return Api.calculators.dates.lifeExpectancy(
        'percentage_reduction_in_normal_life_expectancy',
        person,
        {
            percentage: lifeExpectancy.percentageReductionInNormalLifeExpectancy,
            date_of_trial: assumedSettlement,
        }
    ).then((data) => {
        return {
            dateOfDeath: data.dateOfDeath,
            ageAtDeath: data.ageAtDeath,
        };
    });
}

function childDependent(person, lifeExpectancy) {
    if (!lifeExpectancy.child.youthCutOffAge || !lifeExpectancy.child.youthCutOffDate) {
        return;
    }

    return Api.calculators.dates.lifeExpectancy(
        lifeExpectancy.method,
        person,
        lifeExpectancy.child.youthCutOffAge,
    ).then((childDependency) => {
        // This is tightly coupled to Life Expectancy.
        // The child shouldn't have a date of death
        // or age at daeth, just an age and date
        // for the end of dependency.
        const mEndOfChildDependency = moment(childDependency.dateOfDeath, 'DD/MM/YYYY');

        return {
            dateOfDeath: mEndOfChildDependency.format('DD/MM/YYYY'),
            child: {
                youthCutOffDate: mEndOfChildDependency.format('DD/MM/YYYY'),
                youthCutOffAge: roundYears(mEndOfChildDependency.diff(moment(person.dateOfBirth, 'DD/MM/YYYY'), 'years', true)),
                youthYearsLeft: roundYears(mEndOfChildDependency.diff(moment(), 'years', true)),
            },
        };
    });
}

/**
 * Determine a users life expectancy from life expectancy options
 *
 * @param assumedSettlement
 * @param person
 * @param lifeExpectancy
 * @return {Promise}
 */
export const determineLife = (assumedSettlement, person, lifeExpectancy) => new Promise((resolve) => {
    switch (lifeExpectancy.method) {
        // Client side calculations, instant resolve
        case 'years_and_months' :
            resolve(calcYearsAndMonths(person, lifeExpectancy));
            break;
        case 'enter_date' :
            resolve(calcEnterDate(person, lifeExpectancy));
            break;
        case 'age_at_death':
            resolve(enterAgeAtDeath(person, lifeExpectancy));
            break;
        // Server side determined values, resolved after server response
        case 'ogden_table' :
            ogdenValue(assumedSettlement, person, lifeExpectancy).then(resolve);
            break;
        case 'percentage_of_normal_life_expectancy':
            percentageOfNormalLifeExpectancy(assumedSettlement, person, lifeExpectancy).then(resolve);
            break;
        case 'percentage_reduction_in_normal_life_expectancy':
            percentageReductionInNormalLifeExpectancy(assumedSettlement, person, lifeExpectancy).then(resolve);
            break;
        case 'dependent_until_adult':
            ogdenValue(assumedSettlement, person, lifeExpectancy).then(resolve);
            break;
        case 'dependent_until_date':
            ogdenValue(assumedSettlement, person, lifeExpectancy).then(resolve);
            break;
        case 'dependent_until_16':
            childDependent(person, lifeExpectancy).then(resolve);
            break;
        case 'dependent_until_18':
            childDependent(person, lifeExpectancy).then(resolve);
            break;
        case 'dependent_until_21':
            childDependent(person, lifeExpectancy).then(resolve);
            break;
        case 'dependent_until_custom':
            childDependent(person, lifeExpectancy).then(resolve);
            break;
        default:
            resolve(true);
    }
});

/**
 * Calculate a person date of death
 *
 * @param person
 * @return {number|null}
 */
export const calcAgeDeath = (person) => {
    const mDob = moment(person.dateOfBirth, 'DD/MM/YYYY');
    const mDod = moment(person.dateOfDeath, 'DD/MM/YYYY');
    if (mDob.isValid() && mDod.isValid()) {
        return roundYears(mDod.diff(mDob, 'years', true));
    }
    return null;
};

/**
 * Sum a set of periods for a specific key.
 *
 * @param {[]} periods
 * @param {string} key
 */
export const sumPeriodValue = (periods, key) => periods.reduce((total, period) => total + (period[key] || 0), 0);

/**+
 * Extract the total income to used for multiplicand
 *
 * @param {personTemplate} person
 * @param {string} type "net" or "gross"
 *
 * @return {number}
 */
export const extractIncome =
    (person, type) =>
        (person.income.retired ? sumPeriodValue(person.income.periods, `${type}PensionIncome`) : 0)
        + (person.income.employmentType !== 'retired' ? sumPeriodValue(person.income.periods, `${type}Income`) : 0);

/**
 * Has the user selected options for income create than zero.
 *
 * @param {personTemplate} person
 *
 * @return {boolean}
 */
export const incomeValid = (assumedSettlement, person) => {
    const dob = moment(person.dateOfBirth, 'DD/MM/YYYY');
    if (assumedSettlement.diff(dob, 'years') < 21 || isChild(person)) {
        return true;
    }
    return person.income.periods.reduce((valid, period) => {
        return valid
            ? period.pensionIncome > 0 || period.employmentIncome > 0 && !!period.endOfPeriod
            : valid;
    }, true);
};

export const isChild = dependant => moment(dependant.dateOfBirth, 'DD/MM/YYYY').isAfter(moment().subtract(18, 'y'));

export const splitPeriodsForRetirement = (commit, updateMutation, addMutation, previousRetirementDate, retirementDate, periods, end, dependant = false, dependantIndex = 0, type = null) => {

    if (previousRetirementDate === "") {
        previousRetirementDate = retirementDate;
    }
    // If we still don't then leave it
    if (previousRetirementDate === "") {
        return;
    }
    previousRetirementDate = moment(previousRetirementDate, 'DD/MM/YYYY');
    // If no current retirement date.
    if (retirementDate === null) {
        return;
    }
    let mRetirementDate = moment(retirementDate, 'DD/MM/YYYY');
    // If we have 1 period
    if (periods.length === 1 && retirementDate !== "") {
        let periodStart = moment(periods[0].startOfPeriod, 'DD/MM/YYYY');
        let periodEnd = moment(periods[0].endOfPeriod, 'DD/MM/YYYY');
        if (!moment(retirementDate, 'DD/MM/YYYY').isBetween(periodStart, periodEnd)) {
            return;
        }
        //Split the period
        commit(updateMutation, {
            index: 0,
            key: "endOfPeriod",
            value: retirementDate,
            dependant,
            type
        });
        let period = defaultIncomePeriodTemplate({
            start: retirementDate,
            end: end,
        });
        if (addMutation === "ADD_DEPENDANT_INCOME_PERIOD") {
            commit(addMutation, {dependant: dependantIndex, period});
        }
        else if (addMutation === "ADD_INCOME_PERIOD") {
            commit(addMutation, {type, period});
        } else {
            commit(addMutation, period);
        }
        return;
    }
    let min = moment(periods[0].startOfPeriod, 'DD/MM/YYYY');
    let max = moment(periods[periods.length - 1].endOfPeriod, 'DD/MM/YYYY');
    // Look for any periods split on the last date
    periods.forEach((period, index) => {
        let start = moment(period.startOfPeriod, 'DD/MM/YYYY');
        let end = moment(period.endOfPeriod, 'DD/MM/YYYY');
        if (index === 0 && mRetirementDate.isBefore(start)) {
            return;
        }
        if (index === periods.length - 1 && mRetirementDate.isAfter(end)) {
            return;
        }
        if (end.isSame(previousRetirementDate) && mRetirementDate.isAfter(start) && mRetirementDate.isBefore(max)) {
            // move end date
            commit(updateMutation, {
                index,
                key: "endOfPeriod",
                value: retirementDate,
                dependant,
                type
            });
        }
        if (start.isSame(previousRetirementDate) && mRetirementDate.isBefore(end) && mRetirementDate.isAfter(min)) {
            // move start date
            commit(updateMutation, {
                index,
                key: "startOfPeriod",
                value: retirementDate,
                dependant,
                type
            });
        }
    });
};

/**
 * Remove any periods for retirement.
 *
 * @param dispatch
 * @param action
 * @param periods
 * @param previousDate
 * @param currentDate
 * @param args
 * @return {Promise}
 */
export const removePeriodsSplitForTheRetired = (dispatch, action, periods, previousDate, currentDate, args = {}) => {
    if (periods.length <= 1) {
        return;
    }
    const proms = periods.map((period, index) => {
        if (period.startOfPeriod === previousDate || period.startOfPeriod === currentDate) {
            return dispatch(action, {index, ...args});
        }
    });
    return Promise.all(proms);
};


export const updateIncomePeriodsForRetirement = (dispatch, action, periods, retired, args = {}) => {
    const proms = periods.map((period, index) => {
        if (retired && period.employmentIncome > 0) {
            return dispatch(action, {
                key: 'employmentIncome',
                value: 0,
                index,
                ...args
            }).then(() => true);
        }
        return false;
    });
    return Promise.all(proms);
};




// WEBPACK FOOTER //
// ./src/services/personFunctions.js